<template>
    <v-container class="toggle animated" fluid>
        <v-row dense>
            <v-col cols="12" md="4">
                <v-text-field
                    v-model="search"
                    variant="outlined"
                    prepend-inner-icon="mdi-magnify"
                    label="Search"
                    density="comfortable"
                    solo
                    hide-details
                    rounded="lg"
                    color="primary"
                ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
                <v-select
                    v-model="complianceFilter"
                    :items="complianceOptions"
                    label="Filter by Compliance"
                    variant="outlined"
                    rounded="lg"
                    density="comfortable"
                    hide-details
                    solo
                    color="primary"
                ></v-select>
            </v-col>
            <v-col class="text-right" cols="12" md="4">
                <v-btn
                @click="showAddDialog()"
                prepend-icon="mdi-plus"
                class="mt-1 rounded-lg"
                color="routesColor"
                height="40px"
                elevation="2">
                    Add Compliance Set Deadlines
                </v-btn>
            </v-col>
            <v-col cols="12">
                <v-row dense>
                    <v-col cols="12">
                        <v-card class="dt-container mt-2">    
                            <v-data-table
                                :mobile="isMobileOrTablet()"
                                :headers="headers"
                                :items="filteredData"
                                :search="search"
                                :loading="loading"
                                :items-per-page="15"
                                :items-per-page-options="[15,30, 50, 100, -1]"
                            >
                            <template v-slot:loading>
                                <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                            </template>
                            <template v-slot:item.compliance.compliance_name="{ item }">
                                <div class="text-truncate" style="max-width: 200px;">
                                    {{ item.compliance.compliance_name }}
                                </div>
                            </template>
                            <template v-slot:item.deadline="{ item }">
                                <span>{{ formatDate(item.deadline)}} </span>
                            </template>
                            <template v-slot:item.deadline_extension="{ item }">
                                <span>{{ formatDate(item.deadline_extension)}} </span>
                            </template>
                            <template v-slot:item.is_extended="{ item }">
                                <v-chip v-if="item.is_extended == true" size="small" color="green" variant="elevated">
                                    Yes
                                </v-chip>
                                <v-chip v-else size="small" color="gray">
                                    No
                                </v-chip>
                            </template>
                            <template v-slot:item.datecreated="{ item }">
                                <span>{{ formatDateTime(item.datecreated)}} </span>
                            </template>
                            <template v-slot:[`item.action`]="{ item }">
                                    <v-btn @click="showEditDialog(item)" size="small" variant="outlined" text="Edit" color="orange" elevation="3"></v-btn>
                            </template>
                            </v-data-table>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-container>

    <v-dialog v-model="confirmActivate" width="450" persistent>
        <v-card color="secondary" rounded="lg" class="pa-2">
            <v-card-text>
                <div class="font-weight-bold mt-2 text-h6">Are you sure you want activate this semester and schoolyear?</div>
                <div class="font-weight-light mt-2">Note: If you activate this you will also deactivate the previously active semester and schoolyear</div>
            </v-card-text>
            <template v-slot:actions>
            <v-spacer></v-spacer>

            <v-btn @click="confirmActivate = false" :disabled="loadingConfirmation" variant="outlined">
                Cancel
            </v-btn>
            <v-btn :loading="loadingConfirmation" @click="activateItem()" color="darkGreen" variant="elevated">
                Confirm
            </v-btn>
            </template>
        </v-card>
    </v-dialog>

    <SetsDeadlineDialog ref="SetsDeadlineDialog" @refresh="init()"/>
</template>

<script>
import mixins from '@/mixins.js';
import { defineComponent } from 'vue';
import { useSnackbar } from '@/composables/useSnackbar'
import SetsDeadlineDialog from '../Dialogs/SetsDeadlineDialog.vue';

export default defineComponent({
    mixins: [mixins],
    components: {
        SetsDeadlineDialog,
    },
    data() {
        return {
            data: [],
            search: null,
            loading: false,
            complianceFilter: 'All',
            headers: [
                { title: 'Compliance', align: 'start', sortable: true, key: 'compliance.compliance_name' },
                { title: 'Set', align: 'start', sortable: true, key: 'set.set_code' },
                { title: 'Deadline', align: 'start', sortable: true, key: 'deadline' },
                { title: 'Deadline Extension', align: 'start', sortable: true, key: 'deadline_extension' },
                { title: 'Extend Deadline', align: 'start', sortable: true, key: 'is_extended' },
                { title: 'Date Created', align: 'start', sortable: true, key: 'datecreated' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ],
            confirmActivate: false,
            loadingConfirmation: false,
            itemToActivate: null,
        }
    },
    computed: {
        complianceOptions() {
            const names = this.data
                .map(item => item.compliance && item.compliance.compliance_name)
                .filter(name => !!name);
            const uniqueNames = [...new Set(names)];
            return ['All', ...uniqueNames];
        },
        filteredData() {
            if (!this.complianceFilter || this.complianceFilter === 'All') {
                return this.data;
            }
            return this.data.filter(item =>
                item.compliance && item.compliance.compliance_name === this.complianceFilter
            );
        }
    },
    methods: {
        async init() {
            this.loading = true;
            const res = await this.axiosCall(`/sets-deadline/findAllBySemesterSchoolyear/${this.$store.state.semester_schoolyear.id}`, 'get');
            if (res.data) {
                this.loading = false;
                this.data = res.data;
            }
        },
        async activateItem() {
            const { showSnackbar } = useSnackbar();
            this.loadingConfirmation = true;
            const res = await this.axiosCall('/semester-schoolyear/activate', 'patch', this.itemToActivate);
            if (res.data && res.data.status == 200) {
                this.init();
                this.loadingConfirmation = false;
                this.confirmActivate = false;
                showSnackbar('Semester and Schoolyear successfully activated')
            }
        },
        showAddDialog() {
            this.$refs.SetsDeadlineDialog.initAdd();
        },
        showEditDialog(data) {
            this.$refs.SetsDeadlineDialog.initUpdate(data);
        },
        showConfirmDialog(data) {
            this.itemToActivate = data;
            this.confirmActivate = true;
        }
    },
    mounted() {
        this.init();
    }
});
</script>

<style scoped>

</style>